import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import WizardStepper from 'components/generic/stepper/WizardStepper'
import { Stack } from '@mui/material'
import { useGetPriceSheetQuery } from 'services/priceSheets'
import PricingFormulaTable from 'components/generic/table/PricingFormulaTable'
import AlertSnackbar from 'components/generic/snackbars'
import Loading from 'components/generic/Loading'
import { CHURN_ZERO_EVENTS as cz, PRICE_TYPES } from 'utils/constants'
import { useUpdatePriceEntryMutation } from 'services/priceEntries'
import { trackEvent } from 'utils/tracking'
import { setSnackBar } from 'state/ui'
import { getFormulaValues } from './commoditiesHelpers'

const EditFormulaModal = ({ onFormStateChange, onHideModal }) => {
  const [title, setTitle] = useState('New Formula')
  const [isUpdate, setIsUpdate] = useState(true)
  const dispatch = useDispatch()
  const { priceEntry, priceSheetId, rowId } = useSelector(state => state.ui.formModalProps)
  const { data: priceSheetData, isFetching: isFetchingPriceSheetData } = useGetPriceSheetQuery(
    { priceSheetId },
    { skip: !priceSheetId },
  )

  const { defaultCurrency, defaultYardUm } = useSelector(state => state.settings)
  const [errorMessage, setErrorMessage] = useState('')
  const [updatePriceEntry, { isLoading }] = useUpdatePriceEntryMutation()

  useEffect(() => {
    if (priceEntry) {
      setTitle(`${priceEntry.materialName}`)
    } else {
      setIsUpdate(false)
    }
  }, [priceEntry])

  const formOptions = { resolver: yupResolver(Yup.object().shape({})) }
  const {
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { isDirty },
  } = useForm(formOptions)
  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const handleSuccess = res => {
    onHideModal()
    trackEvent(cz.UPDATE_PRICING_FORMULA)
    if (res.buyPrice?.referencePriceEntryId) {
      trackEvent({
        eventName: cz.USE_DEFAULT_PRICE,
        description: 'Default Buy Price',
      })
    }
    if (res.sellPrice?.referencePriceEntryId) {
      trackEvent({
        eventName: cz.USE_DEFAULT_PRICE,
        description: 'Default Sell Price',
      })
    }

    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: 'Price Entry successfully updated',
      }),
    )
  }

  const handleError = error => {
    const errorMsg = error?.data?.error || 'An error occurred updating the priceEntry.'
    setErrorMessage(errorMsg)
    trackEvent({ eventName: cz.UPDATE_PRICING_FORMULA, errorMsg })
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
  }

  const handleValidatePartial = () => {
    if (isDirty) return false
    return true
  }

  const onSavePriceFormula = async () => {
    const updatedPriceEntry = getFormulaValues(priceEntry, getValues, defaultCurrency, defaultYardUm)
    const buyPriceType = updatedPriceEntry?.buyPrice?.priceType
    const buyBenchmarkId = updatedPriceEntry?.buyPrice?.benchmarkId
    const buyReferencePriceEntryId = updatedPriceEntry?.buyPrice?.referencePriceEntryId
    const sellPriceType = updatedPriceEntry?.sellPrice?.priceType
    const sellBenchmarkId = updatedPriceEntry?.sellPrice?.benchmarkId
    const sellReferencePriceEntryId = updatedPriceEntry?.sellPrice?.referencePriceEntryId

    if (buyPriceType === PRICE_TYPES.FORMULA.id && !buyBenchmarkId && !buyReferencePriceEntryId) {
      setErrorMessage('Buy Reference Price is required.')
    } else if (sellPriceType === PRICE_TYPES.FORMULA.id && !sellBenchmarkId && !sellReferencePriceEntryId) {
      setErrorMessage('Sell Reference Price is required.')
    } else {
      await updatePriceEntry({
        priceEntryId: updatedPriceEntry.id,
        materialTypeId: updatedPriceEntry.materialTypeId,
        priceEntry: updatedPriceEntry,
      })
        .unwrap()
        .then(handleSuccess)
        .catch(handleError)
    }
  }

  const handleSubmitPartial = async () => {
    if (handleValidatePartial()) return
    onSavePriceFormula()
  }
  const steps = [
    {
      showStepper: false,
      stepOptional: false,
      footerButtonTitles: ['SAVE', 'CANCEL'],
      contentComponent: (
        <Stack sx={{ height: 450 }}>
          <PricingFormulaTable
            isViewMode={false}
            control={control}
            watch={watch}
            setValue={setValue}
            priceEntry={priceSheetData?.entriesObj?.find(({ id }) => id === rowId)}
            isPricelist
          />
        </Stack>
      ),
    },
  ]

  if (isFetchingPriceSheetData) {
    return <Loading />
  }

  return (
    <div>
      <AlertSnackbar
        message={errorMessage}
        isOpen={!!errorMessage}
        severity="error"
        handleClose={() => setErrorMessage('')}
      />
      <form onSubmit={handleSubmit(onSavePriceFormula)}>
        {isLoading && <Loading />}
        <WizardStepper
          steps={steps}
          isWide
          nonLinear={isUpdate}
          title={title}
          validationCheck
          onHideModal={onHideModal}
          onSubmitPartial={handleSubmitPartial}
          onValidatePartial={handleValidatePartial}
        />
      </form>
    </div>
  )
}

EditFormulaModal.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

EditFormulaModal.defaultProps = {
  onFormStateChange: noop,
  onHideModal: noop,
}

export default EditFormulaModal
